import { useDraggable } from '@dnd-kit/core'
import { Event, getPlatformDisplayName } from '@/lib/calendar/types'
import { useState, useRef, useEffect } from 'react'
import { Check } from 'lucide-react'

interface DraggableEventCardProps {
  event: Event
  onEventClick: (event: Event) => void
  onViewPost?: (event: Event) => void
}

export function DraggableEventCard({ event, onEventClick, onViewPost }: DraggableEventCardProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const titleRef = useRef<HTMLDivElement>(null)

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const eventDate = new Date(event.date)
  eventDate.setHours(0, 0, 0, 0)

  const isPastEvent =
    eventDate < today || event.status === 'published' || event.source === 'tracked'

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: event.id,
    data: { event },
    disabled: isPastEvent,
  })

  const [animationDistance, setAnimationDistance] = useState(0)

  useEffect(() => {
    if (titleRef.current) {
      const isOverflowing = titleRef.current.scrollWidth > titleRef.current.clientWidth
      setShouldAnimate(isOverflowing)
      setAnimationDistance(
        isOverflowing ? -(titleRef.current.scrollWidth - titleRef.current.clientWidth) : 0,
      )
    }
  }, [event.title])

  const handleCheckClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onViewPost) {
      onViewPost(event)
    }
  }

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={(e) => {
        e.stopPropagation()
        onEventClick(event)
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`bg-background rounded-md p-2 text-xs transition-all hover:brightness-110 ${
        isDragging ? 'opacity-50' : 'opacity-100'
      } ${isPastEvent ? 'cursor-default' : 'cursor-move'}`}>
      <div className="mb-1 flex items-center gap-1.5 overflow-hidden" ref={titleRef}>
        <div
          className="text-foreground min-w-0 flex-1 overflow-hidden whitespace-nowrap"
          style={{
            transform:
              isHovered && shouldAnimate ? `translateX(${animationDistance}px)` : 'translateX(0)',
            transition: 'transform 4s linear',
          }}>
          {event.title}
        </div>
        {isPastEvent && (
          <div
            className="flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full bg-green-500 transition-colors hover:bg-green-600"
            onClick={handleCheckClick}>
            <Check className="h-2.5 w-2.5 text-white" />
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <div
          className="rounded px-2 py-0.5 text-xs text-white"
          style={{ backgroundColor: event.platformColor }}>
          {getPlatformDisplayName(event.platform)}
        </div>
        <div className="text-muted-foreground text-[10px]">{event.time}</div>
      </div>
    </div>
  )
}
